import { useEffect, useState, useMemo } from "react";
import { getMemState, clearMemState } from "services/memState";

const InitialState = { logs: [], generate_logs: [] };

const getIsLoadingByProcess = (state, process, scope) => {
  const logsByScope = state?.[process]?.filter((log) => log?.scope === scope);

  const lastLog = logsByScope?.length && logsByScope[logsByScope?.length - 1];

  return lastLog?.status === "loading";
};

const useMemState = (initialState, scope) => {
  const [state, updateState] = useState(InitialState);

  const fetchState = async () => {
    const response = await getMemState(scope);
    const newData = response?.data?.state;

    if (newData !== null) {
      updateState(newData);
    }
  };

  /**
   * get updated state on start.
   */
  useEffect(() => {
    fetchState();
  }, []);

  useEffect(() => {
    const data = initialState?.data || null;

    if (data !== null) {
      updateState(data);
    }
  }, [initialState]);

  const isReplicating = useMemo(() => {
    return getIsLoadingByProcess(state, "logs", scope);
  }, [state]);

  const isGeneratingCSV = useMemo(() => {
    return getIsLoadingByProcess(state, "generate_logs", scope);
  }, [state]);

  const isCompleted = useMemo(() => {
    return state?.logs?.[state.logs?.length - 1 || 0]?.status === "completed";
  }, [state?.logs]);

  const isGenerateFileLoading = useMemo(() => {
    return state?.generate_logs?.[state.generate_logs?.length - 1 || 0]?.status === "loading";
  }, [state?.generate_logs]);

  const isGenerateFileCompleted = useMemo(() => {
    return ["completed", "error"].includes(
      state?.generate_logs?.[state.generate_logs?.length - 1 || 0]?.status
    );
  }, [state?.generate_logs]);

  useEffect(() => {
    const intervalId = setInterval(async () => {
      try {
        fetchState();
      } catch (error) {
        console.error("Something went wrong getting data:", error);
      }
    }, 30000);

    if (isCompleted && isGenerateFileCompleted) {
      clearInterval(intervalId);
      return;
    }

    return () => clearInterval(intervalId);
  }, [isReplicating, isGeneratingCSV, state, isCompleted, isGenerateFileCompleted]);

  /**
   * Clean Mem State
   */
  const cleanMemState = async () => {
    const response = await clearMemState(scope);

    updateState(response.data);
  };

  return {
    state,
    cleanMemState,
    isReplicating,
    isGeneratingCSV,
    isCompleted,
    isGenerateFileCompleted,
    isGenerateFileLoading
  };
};

export { useMemState };
